/**
 * Журнал «Голосового стенда»: все разговоры (браузер и звонки по телефону) новыми сверху; клик — карточка
 * разговора (`VoiceLabCallCard.tsx`). `GET /api/voice-lab/calls`; журнал агента — `storage/voice/lab/`.
 * Спека `docs/specs/2026-10-26-voice-lab-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { MODES, OUTCOMES, labCallsQuery, type LabCallRow } from "@/lib/voiceLab";

import { CallCard, длительность, когда, телефон } from "./VoiceLabCallCard";
import { ДОЛГО, rub, usd } from "./VoiceLabParts";

export { телефон };

/** Статус замечаний к звонку — только он, сами замечания в карточке (владелец 27.10.26). */
const СТАТУС_ЗАМЕЧАНИЯ: Record<string, string> = {
  waiting_voice: "Расшифровывается",
  voice_failed: "Голос не расшифровался",
  new: "Новое",
  proposed: "Предложено решение",
  processed: "Обработано",
  applied: "Внесено",
  noted: "Учтено",
  rejected: "Отклонено",
};

/** Отбор по поиску в LightRAG: был ли запрос и нашёлся ли ответ — вопросы, которых нет в базе. */
const ОТБОР = [
  ["", "Все"],
  ["lightrag", "Был поиск в LightRAG"],
  ["empty", "Не нашёл в базе"],
] as const;
const подходит = (r: LabCallRow, отбор: string) =>
  отбор === "lightrag" ? !!r.lightrag?.n : отбор === "empty" ? !!r.lightrag?.empty : true;

const th = "whitespace-nowrap px-2 py-1.5 text-left font-medium text-fg-muted";
const td = "whitespace-nowrap px-2 py-1.5";

function Кому({ r }: { r: LabCallRow }) {
  return (
    <>
      {r.name}
      <span className="ml-1.5 text-fg-muted">
        {r.phone ? `☎ ${телефон(r.phone)}` : r.turn === "text" ? "Telegram · текст" : "браузер"}
      </span>
    </>
  );
}

export function VoiceLabJournal({
  room,
  version,
  open,
  clearVersion,
  watching,
}: {
  room: string | null;
  /** «разговоры на этой версии» из вкладки «Сценарии»: `chat/obzvon/v1` — метки `chat/obzvon/v1:…` */
  version?: string | null;
  open: (room: string | null) => void;
  clearVersion?: () => void;
  watching: boolean;
}) {
  const calls = useQuery({
    ...labCallsQuery(),
    refetchInterval: watching ? 5_000 : 30_000,
  });
  const [отбор, setОтбор] = useState("");
  if (room) return <CallCard room={room} back={() => open(null)} />;
  const все = calls.data?.calls ?? [];
  const rows = все.filter((r) => подходит(r, отбор) && (!version || r.version_key === version));
  const курс = calls.data?.rub?.rate;
  return (
    <section className="flex flex-col gap-2" data-annot="журнал разговоров">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
        <span>
          Разговоров за 30 дней: {все.length}
          {(отбор || version) && ` · в отборе ${rows.length}`}
          {watching && " · идёт звонок — разговор появится здесь, когда он закончится"}
        </span>
        {version && (
          <button
            type="button"
            onClick={clearVersion}
            className="rounded-full border border-accent bg-accent/10 px-2.5 py-px text-fg"
            title="Разговоры одной версии сценария — снять отбор"
            data-annot="отбор журнала по версии"
          >
            Версия {version} ✕
          </button>
        )}
        {ОТБОР.map(([код, имя]) => (
          <button
            key={код}
            type="button"
            onClick={() => setОтбор(код)}
            className={`rounded-full border px-2.5 py-px ${отбор === код ? "border-accent bg-accent/10 text-fg" : "border-border hover:text-fg"}`}
            data-annot="отбор журнала по LightRAG"
          >
            {имя}
          </button>
        ))}
      </div>
      {calls.isError && (
        <div className="text-[12.5px] text-err">Журнал не загрузился</div>
      )}
      <div className="overflow-x-auto">
        <table className="text-[12.5px]">
          <thead>
            <tr className="border-b border-border">
              <th className={th}>Когда</th>
              <th className={th}>Кому</th>
              <th className={th}>Модель · голос</th>
              <th className={th} title="Канал и линия сценария: голос — звонки, чат — переписка бота">
                Сценарий
              </th>
              <th className={th}>Длилось</th>
              <th className={th}>Реплик робота</th>
              <th className={th}>Пауза</th>
              <th className={th}>Итог</th>
              <th className={th} title="Поиск робота в базе знаний LightRAG: запросов · без ответа">
                LightRAG
              </th>
              <th className={th}>Расход</th>
              <th className={th}>Запись</th>
              <th className={th}>Замечания</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.room}
                onClick={() => open(r.room)}
                className="cursor-pointer border-b border-border hover:bg-bg-subtle"
              >
                <td className={`${td} tabular-nums`}>{когда(r.started)}</td>
                <td className={td}>
                  <Кому r={r} />
                </td>
                <td className={td}>
                  {r.model}
                  {r.voice && (
                    <span className="text-fg-muted"> · {r.voice}</span>
                  )}
                  {r.speed != null && r.speed !== 1 && (
                    <span className="text-fg-muted">
                      {" "}
                      ×{r.speed.toFixed(2)}
                    </span>
                  )}
                </td>
                <td className={td} title={`${MODES[r.mode] ?? r.mode}${r.prompt ? ` · ${r.prompt}` : ""}`}>
                  {r.channel === "chat" ? "Чат" : "Голос"}
                  {r.line && <span className="text-fg-muted"> · {r.line}</span>}
                </td>
                <td className={`${td} tabular-nums`}>
                  {длительность(r.seconds)}
                </td>
                <td className={`${td} text-right tabular-nums`}>{r.turns}</td>
                <td
                  className={`${td} tabular-nums ${r.latency_avg != null && r.latency_avg > ДОЛГО ? "text-err" : ""}`}
                >
                  {r.latency_avg != null ? `${r.latency_avg} мс` : "—"}
                </td>
                <td className={td}>
                  {OUTCOMES[r.outcome] ?? r.outcome}
                  {!!r.errors && (
                    <span className="text-err" title="инструмент не уложился в срок или упал — модель получила заглушку">
                      {" "}
                      · ⚠ {r.errors}
                    </span>
                  )}
                </td>
                <td className={`${td} tabular-nums`}>
                  {r.lightrag?.n ? (
                    <>
                      {r.lightrag.n}
                      {!!r.lightrag.empty && <span className="text-warn"> · не нашёл {r.lightrag.empty}</span>}
                    </>
                  ) : (
                    <span className="text-fg-muted">—</span>
                  )}
                </td>
                <td className={`${td} tabular-nums`}>
                  {r.usd != null ? (курс ? rub(r.usd, курс) : usd(r.usd)) : "—"}
                </td>
                <td className={td}>{r.audio ? "▶" : "—"}</td>
                <td className={td}>
                  {r.remarks && Object.keys(r.remarks).length ? (
                    <span className="flex gap-1">
                      {Object.entries(r.remarks).map(([код, n]) => (
                        <span key={код} className="rounded-full border border-border px-1.5 text-[11.5px]">
                          {СТАТУС_ЗАМЕЧАНИЯ[код] ?? код}
                          {n > 1 && ` · ${n}`}
                        </span>
                      ))}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
